/**
 * AudienceBuilder — defines who receives a newsletter: contact types
 * (defaults to Realtors) + optional tag narrowing, with a live recipient
 * count and sample list that exactly match the backend resolver.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Icon,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  FormHelperText,
  Skeleton,
  Avatar,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { FiUsers, FiCheck } from 'react-icons/fi';
import { useAuth } from '../../../../contexts/AuthContext';
import { useCrmSettings } from '../../../../contexts/CrmSettingsContext';
import { countAudience, sampleAudience } from '../../../../api/supabase/newsletters';

const COUNT_DEBOUNCE_MS = 400;

export default function AudienceBuilder({ uid, value, onChange }) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const { contactTypes: allContactTypes, getVisibleTags } = useCrmSettings();

  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const selectedBorder = useColorModeValue('blue.400', 'blue.300');
  const chipBorder = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const countBg = useColorModeValue('blue.50', 'blue.900');

  const audienceConfig = value || { contactTypes: ['real-estate-agent'], tagsAny: [] };
  const selectedTypes = audienceConfig.contactTypes || [];
  const selectedTags = audienceConfig.tagsAny || [];

  const isCRMAdmin = SuperAdmin || (Roles || []).includes('CRM Admin');
  const tagOptions = useMemo(() => {
    const tags = getVisibleTags(currentUser?.uid, isCRMAdmin) || [];
    return tags.map((t) => ({
      value: t.value,
      label: t.displayLabel || t.label || t.value,
    }));
  }, [getVisibleTags, currentUser?.uid, isCRMAdmin]);

  const [count, setCount] = useState(null);
  const [sample, setSample] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    setLoadingPreview(true);
    const timer = setTimeout(async () => {
      try {
        const [liveCount, liveSample] = await Promise.all([
          countAudience(uid, audienceConfig),
          sampleAudience(uid, audienceConfig, 5),
        ]);
        if (!cancelled) {
          setCount(liveCount);
          setSample(liveSample);
        }
      } catch (err) {
        console.error('[AudienceBuilder] Preview failed:', err);
        if (!cancelled) {
          setCount(null);
          setSample([]);
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    }, COUNT_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, JSON.stringify(audienceConfig)]);

  const toggleType = (typeValue) => {
    const next = selectedTypes.includes(typeValue)
      ? selectedTypes.filter((t) => t !== typeValue)
      : [...selectedTypes, typeValue];
    // Never allow an empty selection — fall back to realtors
    onChange({ ...audienceConfig, contactTypes: next.length > 0 ? next : ['real-estate-agent'] });
  };

  return (
    <VStack align="stretch" spacing={4}>
      <FormControl>
        <FormLabel fontSize="sm">Contact types</FormLabel>
        <Wrap spacing={2}>
          {(allContactTypes || []).map((type) => {
            const isSelected = selectedTypes.includes(type.value);
            return (
              <WrapItem key={type.value}>
                <Tag
                  size="md"
                  borderRadius="full"
                  variant="outline"
                  cursor="pointer"
                  onClick={() => toggleType(type.value)}
                  bg={isSelected ? selectedBg : 'transparent'}
                  borderColor={isSelected ? selectedBorder : chipBorder}
                  boxShadow="none"
                  borderWidth="1.5px"
                  px={3}
                  py={1.5}
                >
                  {isSelected && <Icon as={FiCheck} boxSize={3} mr={1.5} color="blue.400" />}
                  <TagLabel fontSize="xs">{type.label}</TagLabel>
                </Tag>
              </WrapItem>
            );
          })}
        </Wrap>
        <FormHelperText fontSize="xs">
          Realtors (real estate agents) are the classic newsletter audience — add other types if needed.
        </FormHelperText>
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm">Narrow by tags (optional)</FormLabel>
        <Select
          isMulti
          size="sm"
          placeholder="Any tag — leave empty to include all"
          options={tagOptions}
          value={tagOptions.filter((o) => selectedTags.includes(o.value))}
          onChange={(selected) =>
            onChange({ ...audienceConfig, tagsAny: (selected || []).map((o) => o.value) })
          }
          chakraStyles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        />
        <FormHelperText fontSize="xs">
          When tags are selected, only contacts with at least one of them are included.
        </FormHelperText>
      </FormControl>

      {/* Live preview */}
      <Box bg={countBg} borderRadius="lg" p={4}>
        <HStack spacing={3} mb={sample.length > 0 ? 3 : 0}>
          <Icon as={FiUsers} boxSize={5} color="blue.400" />
          <Skeleton isLoaded={!loadingPreview} minW="200px" borderRadius="md">
            <Text fontSize="sm" fontWeight="semibold">
              {count === null ? 'Audience unavailable' : `${count} recipient${count === 1 ? '' : 's'} right now`}
            </Text>
          </Skeleton>
        </HStack>
        {sample.length > 0 && (
          <VStack align="stretch" spacing={1.5}>
            {sample.map((contact) => (
              <Flex key={contact.id} align="center" gap={2}>
                <Avatar size="2xs" name={contact.name || contact.email} />
                <Text fontSize="xs" fontWeight="medium">{contact.name || '—'}</Text>
                <Text fontSize="xs" color={mutedColor}>{contact.email}</Text>
              </Flex>
            ))}
            {count > sample.length && (
              <Text fontSize="10px" color={mutedColor}>…and {count - sample.length} more</Text>
            )}
          </VStack>
        )}
      </Box>

      <Alert status="info" borderRadius="md" fontSize="xs" py={2}>
        <AlertIcon boxSize={4} />
        Contacts without an email, unsubscribed contacts, and anyone marked Do Not Contact are
        excluded automatically — at preview time and again at send time.
      </Alert>
    </VStack>
  );
}
